import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "./Logo";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/domes", label: "The Domes" },
  { to: "/experiences", label: "Experiences" },
  { to: "/weddings", label: "Weddings & Events" },
  { to: "/about", label: "Our Story" },
  { to: "/contact", label: "Contact" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header className="fixed top-5 left-1/2 -translate-x-1/2 z-50 w-[92%] max-w-6xl">
        <div
          className={`flex items-center justify-between rounded-full px-5 py-3 border transition-colors duration-500 ${
            scrolled || open
              ? "bg-ink/85 backdrop-blur-xl border-white/10"
              : "bg-ink/40 backdrop-blur-md border-white/10"
          }`}
        >
          <Link to="/" aria-label="Danseys Pass Lavender Farm — home" data-testid="nav-logo-link">
            <Logo />
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
                className={({ isActive }) =>
                  `link-underline font-mono text-[0.68rem] uppercase tracking-[0.18em] ${
                    isActive ? "active text-lavglow" : "text-sage hover:text-cream"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/book"
              data-testid="nav-book-button"
              className="group inline-flex items-center gap-2 bg-cream text-ink rounded-full px-5 py-2.5 font-mono text-[0.68rem] uppercase tracking-[0.18em] font-medium transition-colors hover:bg-lavglow"
            >
              Book your stay
              <ArrowUpRight size={13} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </nav>

          <button
            className="lg:hidden text-cream p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
            data-testid="nav-mobile-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-40 bg-ink/95 backdrop-blur-xl flex flex-col justify-center px-8 lg:hidden"
            data-testid="mobile-menu-overlay"
          >
            <nav className="flex flex-col gap-5">
              {[...LINKS, { to: "/book", label: "Book your stay" }].map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
                    className={({ isActive }) =>
                      `font-display text-4xl ${isActive ? "text-lavglow italic" : "text-cream"}`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <p className="absolute bottom-10 left-8 font-mono text-[0.6rem] uppercase tracking-widest2 text-fern">
              North Otago · Aotearoa New Zealand
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
